import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import { Typography, Button, Box } from '@mui/material'
import { LogIn, UserPlus } from 'react-feather'
import { useTheme } from '@mui/material/styles'
import { LOGIN_PATH } from '../../routes/LoginRoutes'
import { HOME_PATH } from '../../routes/HomeRoutes'
import { ONBOARDING_PATH } from '../../routes/OnboardingRoutes'
import InputTextField from '../../components/InputTextField/InputTextField'
import PhoneNumberMask from '../../components/PhoneNumberMask/PhoneNumberMask'
import HtmlTooltip from '../../components/HtmlTooltip/HtmlTooltip'
import { validateEmailAddress, validatePhoneNumber } from '../../utils/Validators/Validators'
import useFirebaseAuthentication from '../../utils/Firebase/hooks/useFirebaseAuthentication'
import { User } from 'firebase/auth'
import useFetchUserByCheckoutSessionId from '../../api/onboarding/useFetchUserByCeckoutSessionId'
import { useAppDispatch, useAppSelector } from '../../store/utils/hooks'
import { actionCreateUser, actionSaveOnboardingData, selectOnboardingData } from '../../store/slices/user/userSlice'
import { userSignOut } from '../../utils/Firebase/methods'
import { t } from 'i18next'
import LoginForm from '../../pages/Login/components/LoginForm/LoginForm'
import { handleFirebaseAuthError } from '../../utils/Firebase/errorHandler'

// Layout component that mimics the onboarding layout
const ManVsMachineLayout: React.FC<{ children: React.ReactNode; navigateLogin: () => void }> = ({
	children,
	navigateLogin,
}) => {
	const { palette } = useTheme()

	return (
		<Stack sx={{ height: '100vh' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', padding: '30px' }}>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<img
						src={`${process.env.PUBLIC_URL}/assets/images/new_prop_p.svg`}
						alt="Logo"
						style={{ maxWidth: '100%', height: '25px' }}
					/>
					<Typography
						component="h1"
						sx={{
							fontSize: '20px',
							fontFamily: '"Eczar", serif',
							margin: '0 10px',
						}}
					>
						{t('prop_ai')}
					</Typography>
				</Box>
				<Box sx={{ display: 'flex', margin: '0 0 0 auto', color: palette.text.darkBlue }}>
					<Button variant="text" onClick={navigateLogin}>
						<LogIn />
						<Typography marginLeft="8px" fontSize="14px" fontWeight="bold">
							{t('onboarding.layout.sign_in_button_label')}
						</Typography>
					</Button>
				</Box>
			</Box>
			<Grid container sx={{ marginBottom: 'auto', flexGrow: 1, alignItems: 'center' }}>
				<Grid item xs={1} />
				<Grid item xs={10}>
					{children}
				</Grid>
				<Grid item xs={1} />
			</Grid>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					marginTop: 'auto',
					gap: '32px',
					fontSize: '14px',
					height: '80px',
					minHeight: '80px',
					backgroundColor: palette.sidebar.gray,
					color: palette.text.darkBlue,
				}}
			>
				<Typography alignContent="center">{t('onboarding.layout.contact_us_link_label')}</Typography>
				<Typography alignContent="center">{t('onboarding.layout.privacy_and_terms_link_label')}</Typography>
				<Typography alignContent="center">{t('onboarding.layout.trademark_link_label')}</Typography>
			</Box>
		</Stack>
	)
}

export default function ManVsMachineRegistrationPage() {
	const dispatch = useAppDispatch()
	const { palette } = useTheme()
	const navigate = useNavigate()
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const checkoutSessionId = searchParams.get('session_id')
	const { loading, authUser } = useFirebaseAuthentication() as {
		loading: boolean
		authUser: (User & { reloadUserInfo?: { localId: string } }) | null
	}
	const onboardingData = useAppSelector(selectOnboardingData)

	// Get checkout data from navigation state, fallback to API call if not available
	const checkoutDataFromState = location.state?.checkoutData
	const { data: checkoutDataFromApi, isLoading } = useFetchUserByCheckoutSessionId(checkoutSessionId)
	const checkoutData = checkoutDataFromState || checkoutDataFromApi

	// Form state
	const [email, setEmail] = useState('')
	const [phoneNumber, setPhoneNumber] = useState('')
	const [firstName, setFirstName] = useState('')
	const [lastName, setLastName] = useState('')
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [canSubmit, setCanSubmit] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [phoneNumberModified, setPhoneNumberModified] = useState(false)
	const [passwordTouched, setPasswordTouched] = useState(false)
	const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false)

	const wrongUser = !!(
		checkoutData?.user_id &&
		authUser?.reloadUserInfo?.localId &&
		authUser?.reloadUserInfo?.localId != checkoutData?.user_id
	)

	// Form validation
	useEffect(() => {
		const validEmail = !!checkoutData?.email || validateEmailAddress(email)
		const validPhone = (!!checkoutData?.phone_number && !phoneNumberModified) || validatePhoneNumber(phoneNumber)
		const validFirstName = firstName.trim().length > 0
		const validLastName = lastName.trim().length > 0
		const validPassword = password.length >= 8
		const validConfirmPassword = password === confirmPassword && password.length > 0

		setCanSubmit(
			validEmail && validPhone && validFirstName && validLastName && validPassword && validConfirmPassword,
		)
	}, [
		email,
		phoneNumber,
		firstName,
		lastName,
		password,
		confirmPassword,
		checkoutData?.email,
		checkoutData?.phone_number,
		phoneNumberModified,
	])

	// Prefill form with checkout data
	useEffect(() => {
		if (checkoutData) {
			setEmail(checkoutData.email || '')
			if (checkoutData.phone_number) {
				const digitsOnly = checkoutData.phone_number.replace(/\D/g, '')
				const last10 = digitsOnly.slice(-10)
				setPhoneNumber(last10)
			} else {
				setPhoneNumber('')
			}

			if (checkoutData.firstName && checkoutData.lastName) {
				setFirstName(checkoutData.firstName)
				setLastName(checkoutData.lastName)
			} else if (checkoutData.name && checkoutData.name.includes(' ')) {
				const nameParts = checkoutData.name.split(' ')
				setFirstName(nameParts[0])
				setLastName(nameParts.slice(1).join(' '))
			} else if (checkoutData.name) {
				setFirstName(checkoutData.name)
			}
		}
	}, [checkoutData])

	// Save onboarding data when checkout data is available
	useEffect(() => {
		if (checkoutData) {
			console.log('checkoutData', checkoutData)
			if (checkoutData.name && checkoutData.name.includes(' ')) {
				const firstName = checkoutData.name.split(' ')[0]
				const lastName = checkoutData.name.split(' ')[1]
				dispatch(
					actionSaveOnboardingData({
						email: checkoutData.email,
						phoneNumber: checkoutData.phone_number,
						firstName: firstName,
						lastName: lastName,
					}),
				)
			} else if (checkoutData.name) {
				dispatch(
					actionSaveOnboardingData({
						email: checkoutData.email,
						phoneNumber: checkoutData.phone_number,
						firstName: checkoutData.name,
					}),
				)
			}
		}
	}, [dispatch, checkoutData])

	const handleCreateAccount = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!canSubmit) return

		setIsSubmitting(true)

		try {
			await dispatch(actionCreateUser({ ...onboardingData, password }))
			navigate(HOME_PATH)
		} catch (error: any) {
			// Provide user-friendly feedback
			console.error('Account creation failed:', error)
			alert(handleFirebaseAuthError(error))
		} finally {
			setIsSubmitting(false)
		}
	}

	const navigateLogin = () => {
		navigate(LOGIN_PATH)
	}

	const getRegisterForm = (wrongUser: boolean) => {
		if (wrongUser) {
			return (
				<Stack gap="16px" width="100%" maxWidth="360px">
					<Typography variant="h5" sx={{ fontWeight: 700 }}>
						Account Mismatch
					</Typography>
					<Typography variant="body1" sx={{ fontWeight: 400 }}>
						Switch to the account associated with {checkoutData?.email}.
					</Typography>
					<Button
						variant="contained"
						onClick={async () => {
							await userSignOut()
							navigate(LOGIN_PATH)
						}}
						startIcon={<LogIn />}
						sx={{ height: '56px', backgroundColor: palette.brand.main }}
						fullWidth
					>
						Logout
					</Button>
				</Stack>
			)
		} else if (!authUser && checkoutData?.user_id) {
			return (
				<Stack gap="16px" width="100%" maxWidth="360px">
					<Typography variant="h5" sx={{ fontWeight: 700 }}>
						Login to your account
					</Typography>
					<LoginForm
						navigate={() => navigate(LOGIN_PATH)}
						switchToCreate={() => navigate(ONBOARDING_PATH)}
						prefillEmail={checkoutData?.email}
					/>
				</Stack>
			)
		} else if (!authUser || !checkoutData) {
			// ----- Field-level validation & visual feedback -----
			const firstNameError = {
				value: firstName.trim().length === 0,
				message: firstName.trim().length === 0 ? 'First name is required' : undefined,
			}
			const lastNameError = {
				value: lastName.trim().length === 0,
				message: lastName.trim().length === 0 ? 'Last name is required' : undefined,
			}
			const emailError = {
				value: !checkoutData?.email && email.length > 0 && !validateEmailAddress(email),
				message:
					!checkoutData?.email && email.length > 0 && !validateEmailAddress(email)
						? 'Invalid email address'
						: undefined,
			}
			const phoneError = {
				value: phoneNumberModified && phoneNumber.length > 0 && !validatePhoneNumber(phoneNumber),
				message:
					phoneNumberModified && phoneNumber.length > 0 && !validatePhoneNumber(phoneNumber)
						? 'Invalid phone number'
						: undefined,
			}
			const passwordError = {
				value: passwordTouched && password.length > 0 && password.length < 8,
				message:
					passwordTouched && password.length > 0 && password.length < 8
						? 'Password must be at least 8 characters'
						: undefined,
			}
			const confirmPasswordError = {
				value: confirmPasswordTouched && confirmPassword.length > 0 && confirmPassword !== password,
				message:
					confirmPasswordTouched && confirmPassword.length > 0 && confirmPassword !== password
						? 'Passwords do not match'
						: undefined,
			}
			return (
				<Stack gap="16px" width="100%" maxWidth="360px">
					{/* Registration Form */}
					<Box component="form" onSubmit={handleCreateAccount} sx={{ width: '100%' }}>
						<Stack gap="16px">
							<Stack direction="row" gap="16px">
								<InputTextField
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									i18nLabel="First Name"
									name="firstName"
									required
									sx={{ flexGrow: 1 }}
									error={firstNameError}
								/>
								<InputTextField
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									i18nLabel="Last Name"
									name="lastName"
									required
									sx={{ flexGrow: 1 }}
									error={lastNameError}
								/>
							</Stack>
							<HtmlTooltip
								placement="right-start"
								title={
									<Typography color="error">
										We need you to use the same email address when registering. Don't worry: you can
										change your email address after registration!
									</Typography>
								}
							>
								<span style={{ display: 'block', width: '100%' }}>
									<InputTextField
										value={email}
										type="email"
										onChange={(e) => setEmail(e.target.value)}
										i18nLabel="Email Address"
										name="email"
										required
										disabled={!!checkoutData?.email}
										error={emailError}
									/>
								</span>
							</HtmlTooltip>
							<InputTextField
								value={phoneNumber}
								onChange={(e) => {
									setPhoneNumber(e.target.value)
									setPhoneNumberModified(true)
								}}
								i18nLabel="Phone Number"
								name="phoneNumber"
								required
								inputComponent={PhoneNumberMask}
								error={phoneError}
							/>
							<InputTextField
								value={password}
								type="password"
								onChange={(e) => {
									setPassword(e.target.value)
									setPasswordTouched(true)
								}}
								i18nLabel="Password"
								name="password"
								required
								error={passwordError}
								sx={{
									'& .MuiOutlinedInput-root': {
										'& fieldset': {
											borderColor: passwordError.value ? 'red' : undefined,
										},
									},
								}}
							/>
							<InputTextField
								value={confirmPassword}
								type="password"
								onChange={(e) => {
									setConfirmPassword(e.target.value)
									setConfirmPasswordTouched(true)
								}}
								i18nLabel="Confirm Password"
								name="confirmPassword"
								required
								error={confirmPasswordError}
								sx={{
									'& .MuiOutlinedInput-root': {
										'& fieldset': {
											borderColor: confirmPasswordError.value ? 'red' : undefined,
										},
									},
								}}
							/>
							<Button
								type="submit"
								variant="contained"
								disabled={!canSubmit || isSubmitting}
								startIcon={isSubmitting ? <CircularProgress size={20} /> : <UserPlus />}
								sx={{
									height: '56px',
									backgroundColor: palette.brand.main,
									'&:disabled': {
										backgroundColor: '#BDBDBD',
									},
								}}
								fullWidth
							>
								{isSubmitting ? 'Creating Account...' : 'Create Account'}
							</Button>
						</Stack>
					</Box>
				</Stack>
			)
		} else {
			return (
				<Stack gap="16px" width="100%" maxWidth="360px">
					<Typography variant="h5" sx={{ fontWeight: 700 }}>
						Your account is ready!
					</Typography>
					<Typography variant="body1" sx={{ color: 'text.secondary' }}>
						Please wait while we redirect you...
					</Typography>
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
						<CircularProgress />
					</Box>
				</Stack>
			)
		}
	}

	// Loading state - only show if we don't have data from state and API is loading
	if ((loading || isLoading) && !checkoutDataFromState) {
		return (
			<Box
				sx={{
					height: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<CircularProgress />
			</Box>
		)
	}

	return (
		<ManVsMachineLayout navigateLogin={navigateLogin}>
			<Grid container justifyContent="center" spacing={{ xs: 2, md: 4 }}>
				<Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', justifyContent: 'center' }}>
					{/* Information Container */}
					<Stack gap="16px" width="100%" maxWidth="360px" alignItems="center">
						<Box>
							<img
								src="/assets/images/onboarding/ringing_phone.svg"
								alt=""
								style={{ maxWidth: '100%', height: 'auto' }}
							/>
						</Box>
						<Box textAlign="center">
							<Typography
								variant="h1"
								sx={{
									fontFamily: '"Eczar", serif',
									fontSize: '44px',
									color: palette.text.darkBlue,
									fontWeight: '500',
									mb: 2,
								}}
							>
								Welcome to
							</Typography>
							<Typography
								variant="h1"
								sx={{
									fontFamily: '"Eczar", serif',
									fontSize: '44px',
									color: palette.brand.main,
									fontWeight: '500',
									mb: 3,
								}}
							>
								prop.ai
							</Typography>
						</Box>
						<Box textAlign="center">
							{checkoutData?.user_id ? (
								<>
									<Typography sx={{ fontSize: '14px', mb: 2 }}>
										<b style={{ color: palette.brand.main }}>Welcome!</b> We've found your existing
										account.
									</Typography>
									<Typography sx={{ fontSize: '14px', mb: 2 }}>
										<b style={{ color: palette.brand.main }}>Your call quota is recharged.</b>{' '}
										You're back in business!
									</Typography>
									<Typography sx={{ fontSize: '14px' }}>
										<b style={{ color: palette.brand.main }}>Don't forget to check your email!</b>{' '}
										We will be in touch with your pairing party invite shortly.
									</Typography>
								</>
							) : (
								<>
									<Typography sx={{ fontSize: '14px', mb: 2 }}>
										<b style={{ color: palette.brand.main }}>
											Welcome {checkoutData?.firstName || checkoutData?.name?.split(' ')[0]}!
										</b>{' '}
										This is the beginning of a great new journey.
									</Typography>
									<Typography sx={{ fontSize: '14px', mb: 2 }}>
										<b style={{ color: palette.brand.main }}>It all begins here!</b> We will be in
										touch with your pairing party invite shortly.
									</Typography>
									<Typography sx={{ fontSize: '14px', mb: 2 }}>
										<b style={{ color: palette.brand.main }}>Let's get calling!</b> Complete your
										registration to create your account.
									</Typography>
								</>
							)}
						</Box>
					</Stack>
				</Grid>
				<Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', justifyContent: 'center' }}>
					{getRegisterForm(wrongUser)}
				</Grid>
			</Grid>
		</ManVsMachineLayout>
	)
}
